import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useCallback, useEffect, useId, useRef, useState, type ReactNode } from 'react'
import { Link } from 'react-router-dom'
import { useApp } from '@/app/AppContext'
import { Icon } from '@/components/Icon'
import type { Locale } from '@/i18n'
import { ApiError } from '@/lib/api'
import { formatDate, formatMoney, formatNumber } from '@/lib/format'
import { fetchPage } from '@/lists/api'
import { CellValue, cardHref } from '@/lists/cellValue'
import { StateView } from './record'
import { initials, labelOf } from './recordHelpers'
import '@/styles/hr.css'

/**
 * Общие части кадровых карточек на дизайн-системе (Employee, Department, Vacancy,
 * LeaveRequest, EmployeeKpi .dc.html).
 *
 * ## Общее правило этих экранов
 *
 * **Ни одна кадровая цифра здесь не считается на клиенте.** Остаток отпуска, покрытие
 * подразделения, процент выполнения KPI, коэффициент и сумма премии, конверсия воронки
 * приходят с сервера. Повторив формулу для отображения, мы завели бы второй ответ на вопрос
 * «сколько дней осталось», и разошёлся бы он молча. Клиент считает только календарь: сколько
 * дней между двумя датами и какой из них выходной, — это показ дат, а не учёт.
 *
 * Пустое значение — прочерк, а не ноль: оклад приходит `null` без полевого права, и ноль
 * читался бы как «не платят» — утверждение, которого мы не делали.
 */

export type Translate = (key: string, params?: Record<string, string | number>) => string

/** Дата `YYYY-MM-DD` в формате языка; пустая — прочерк. */
export function day(value: string | null | undefined, locale: Locale): string {
  return value ? formatDate(value, locale) : '—'
}

/** Деньги; `null` — прочерк (нет права либо не назначено), не ноль. */
export function money(value: number | null | undefined, currency: string | null | undefined, locale: Locale): string {
  if (value == null || value === undefined) return '—'
  // Without a currency the sum is shown bare, never as roubles (#91).
  return formatMoney(value, currency ?? null, locale)
}

/** Число; `null` — прочерк по той же причине, что и деньги. */
export function num(value: number | null | undefined, locale: Locale): string {
  if (value == null || value === undefined) return '—'
  return formatNumber(value, locale)
}

/** Процент; `null` — прочерк: «не с чем сравнивать» не равно «ноль процентов». */
export function percent(value: number | null | undefined, locale: Locale): string {
  if (value == null || value === undefined) return '—'
  return `${formatNumber(value, locale)} %`
}

/**
 * Значение KPI в единице показателя.
 *
 * План и факт одной строки обязаны читаться в одной единице: маржа, показанная голыми
 * числами, выглядела как «план 18, факт 1 240 000» — сравнение несопоставимого (найдено
 * наполнением стенда; единицу показателя сервер отдаёт в `unit`). Код валюты — деньги, `%` —
 * процент, прочее — число с единицей. Без единицы — голое число: выдуманная единица хуже
 * отсутствующей.
 */
export function kpiValue(value: number | null | undefined, unit: string | null | undefined, locale: Locale): string {
  if (value == null || value === undefined) return '—'
  if (!unit) return formatNumber(value, locale)
  if (/^[A-Z]{3}$/.test(unit)) return formatMoney(value, unit, locale)
  if (unit === '%') return percent(value, locale)
  return `${formatNumber(value, locale)} ${unit}`
}

/** Короткая форма идентификатора — когда сервер не дал названия, а выдумать его нельзя. */
export function short(id: string | null | undefined): string {
  return id ? id.slice(0, 8) : '—'
}

/** Строковое значение колонки записи; пустая строка и отсутствие — `null`. */
export function text(values: Record<string, unknown>, column: string): string | null {
  const raw = values[column]
  return typeof raw === 'string' && raw !== '' ? raw : null
}

/** Числовое значение колонки записи. */
export function number(values: Record<string, unknown>, column: string): number | null {
  const raw = values[column]
  if (typeof raw === 'number') return raw
  if (typeof raw === 'string' && raw.trim() !== '' && !Number.isNaN(Number(raw))) return Number(raw)
  return null
}

/** Сегодня в местном поясе, `YYYY-MM-DD`. */
export function today(now: Date = new Date()): string {
  return isoDate(now)
}

export function isoDate(date: Date): string {
  const pad = (value: number) => String(value).padStart(2, '0')
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`
}

/** Календарная дата → местная полночь (не UTC: иначе западнее Гринвича день съезжает). */
export function parseDay(value: string): Date | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value)
  if (!match) return null
  return new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]))
}

/** Календарных дней от `from` до `to` включительно; `null`, если дата не разбирается. */
export function calendarDays(from: string, to: string): number | null {
  const start = parseDay(from)
  const end = parseDay(to)
  if (!start || !end) return null
  return Math.round((end.getTime() - start.getTime()) / 86_400_000) + 1
}

/** Первый и последний день текущего месяца — период по умолчанию в формах премии. */
export function currentMonth(now: Date = new Date()): { from: string; to: string } {
  return {
    from: isoDate(new Date(now.getFullYear(), now.getMonth(), 1)),
    to: isoDate(new Date(now.getFullYear(), now.getMonth() + 1, 0)),
  }
}

/** Текст отказа сервера — им и показывается отказ, а не общим «ошибка». */
export function failureText(error: unknown, t: Translate): string {
  return error instanceof ApiError ? error.message : t('app.error')
}

/**
 * Загрузка карточки (`.skel`): форма будущего экрана, а не крутилка — пользователь видит,
 * где появятся шапка, факты и вкладки.
 */
export function HrSkeleton({ label, avatar }: { label: string; avatar?: boolean }) {
  return (
    <div className="rec-page" aria-busy="true" aria-label={label}>
      <div className="row" style={{ gap: 16 }}>
        {avatar && <span className="skel skel-c" style={{ width: 64, height: 64 }} />}
        <div className="stack" style={{ gap: 8, flex: 1 }}>
          <span className="skel" style={{ width: 320, height: 20 }} />
          <span className="skel" style={{ width: 460 }} />
        </div>
      </div>
      <span className="skel" style={{ height: 62, borderRadius: 8 }} />
      <span className="skel" style={{ height: 36, borderRadius: 6 }} />
      <span className="skel" style={{ height: 220, borderRadius: 8 }} />
    </div>
  )
}

/**
 * Отказ загрузки документа карточки: «нет доступа» и «ошибка» называются словами и ведут к
 * разным действиям. Текст сервера показывается как есть — он объясняет, а не пугает.
 */
export function HrFailure({
  error,
  listTo,
  onRetry,
}: {
  error: unknown
  listTo: string
  onRetry: () => void
}) {
  const { t } = useApp()
  const forbidden = error instanceof ApiError && error.isForbidden
  return (
    <StateView
      boxed
      kind={forbidden ? 'forbidden' : 'error'}
      title={forbidden ? t('record.state.forbidden.title') : t('record.state.error.title')}
      description={error instanceof ApiError && error.message ? error.message : t('record.state.error.text')}
      actions={
        <>
          {!forbidden && (
            <button type="button" className="btn btn-primary btn-sm" onClick={onRetry}>
              <Icon name="refresh" size="sm" />
              {t('record.state.retry')}
            </button>
          )}
          <Link to={listTo} className="btn btn-ghost btn-sm">
            {t('record.state.toList')}
          </Link>
        </>
      }
      code={error instanceof ApiError ? String(error.status) : undefined}
    />
  )
}

/** Состояние раздела внутри панели: загрузка, отказ словами, затем данные. */
export function Section<T>({
  query,
  errorTitle,
  children,
}: {
  query: { isLoading: boolean; error: unknown; data: T | undefined }
  errorTitle: string
  children: (data: T) => ReactNode
}) {
  const { t } = useApp()
  if (query.isLoading) return <div className="panel-b muted">{t('app.loading')}</div>
  if (query.error || query.data === undefined) {
    const forbidden = query.error instanceof ApiError && query.error.isForbidden
    return (
      <StateView
        compact
        kind={forbidden ? 'forbidden' : 'error'}
        title={forbidden ? t('record.state.forbidden.title') : errorTitle}
        description={query.error instanceof ApiError ? query.error.message : undefined}
      />
    )
  }
  return <>{children(query.data)}</>
}

/** Результат операции: успех и отказ сервера одной полосой над вкладками. */
export function useOutcome(invalidate: unknown[][]) {
  const { t } = useApp()
  const queryClient = useQueryClient()
  const [failure, setFailure] = useState<string | null>(null)
  const [notice, setNotice] = useState<string | null>(null)
  const keys = JSON.stringify(invalidate)

  const succeed = useCallback(
    (message: string) => {
      setFailure(null)
      setNotice(message)
      for (const key of JSON.parse(keys) as unknown[][]) {
        void queryClient.invalidateQueries({ queryKey: key })
      }
    },
    [queryClient, keys],
  )
  const fail = useCallback(
    (error: unknown) => {
      setNotice(null)
      setFailure(failureText(error, t))
    },
    [t],
  )
  return { failure, notice, succeed, fail }
}

export function OutcomeAlerts({ failure, notice }: { failure: string | null; notice: string | null }) {
  return (
    <>
      {failure && (
        <div className="alert alert-danger" role="alert">
          <Icon name="alert" />
          <span>{failure}</span>
        </div>
      )}
      {notice && (
        <div className="alert alert-success" role="status">
          <Icon name="check" />
          <span>{notice}</span>
        </div>
      )}
    </>
  )
}

/**
 * Окно формы (`.modal` с `.modal-b.is-form`): перевод, увольнение, премия, отказ.
 *
 * Esc закрывает без сохранения, фокус — в первое поле: окно открывают, чтобы заполнить.
 * Необратимое подтверждение без полей — это `ConfirmModal`, а не это окно.
 */
export function FormModal({
  open,
  title,
  subtitle,
  children,
  footer,
  onClose,
  danger,
  sheet,
  overline,
}: {
  open: boolean
  title: ReactNode
  subtitle?: ReactNode
  children: ReactNode
  footer: ReactNode
  onClose: () => void
  /** Необратимое (увольнение): `alertdialog` и иконка предупреждения. */
  danger?: boolean
  /** Боковая панель (`.sheet`) вместо окна — для длинной формы (перевод). */
  sheet?: boolean
  /** Надпись над заголовком панели (`.overline`). */
  overline?: ReactNode
}) {
  const { t } = useApp()
  const titleId = useId()
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!open) return
    const opener = document.activeElement as HTMLElement | null
    // Necessary-but-irreversible forms put focus on "Cancel" (data-autofocus), the rest on
    // the first field: the window was opened to be filled in.
    const target =
      ref.current?.querySelector<HTMLElement>('[data-autofocus]') ??
      ref.current?.querySelector<HTMLElement>('input, textarea, select')
    target?.focus()
    return () => opener?.focus?.()
  }, [open])

  if (!open) return null
  const onKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Escape') {
      event.stopPropagation()
      onClose()
    }
  }
  if (sheet) {
    return (
      <div className="modal-layer hr-sheet-layer" onKeyDown={onKeyDown}>
        <aside ref={ref as React.RefObject<HTMLElement>} className="sheet" role="dialog" aria-modal="true" aria-labelledby={titleId}>
          <div className="sheet-h">
            <div className="stack" style={{ flex: 1, gap: 2 }}>
              {overline && <span className="overline">{overline}</span>}
              <span id={titleId} className="h2">
                {title}
              </span>
              {subtitle && <span className="t-sm muted">{subtitle}</span>}
            </div>
            <button type="button" className="btn btn-ghost btn-sm btn-icon" aria-label={t('record.hr.close')} onClick={onClose}>
              <Icon name="close" />
            </button>
          </div>
          <div className="sheet-b">{children}</div>
          <div className="sheet-f">
            <span className="hint">{t('record.hr.escHint')}</span>
            {footer}
          </div>
        </aside>
      </div>
    )
  }
  return (
    <div className="modal-layer" onKeyDown={onKeyDown}>
      <div
        ref={ref}
        className="modal modal-lg"
        role={danger ? 'alertdialog' : 'dialog'}
        aria-modal="true"
        aria-labelledby={titleId}
      >
        <div className="modal-h" style={{ paddingBottom: 12 }}>
          {danger && (
            <span className="modal-ico">
              <Icon name="alert" />
            </span>
          )}
          <div className="stack" style={{ gap: 2, flex: 1, paddingTop: danger ? 5 : 0 }}>
            <span id={titleId} className="h2" style={{ fontSize: 15 }}>
              {title}
            </span>
            {subtitle && <span className="t-sm muted">{subtitle}</span>}
          </div>
          <button type="button" className="btn btn-ghost btn-sm btn-icon" aria-label={t('record.hr.close')} onClick={onClose}>
            <Icon name="close" />
          </button>
        </div>
        <div className="modal-b is-form stack" style={{ gap: 14 }}>
          {children}
        </div>
        <div className="modal-f">{footer}</div>
      </div>
    </div>
  )
}

/** Поле формы с подписью (`.field` / `.label` / `.help`). */
export function Field({
  id,
  label,
  required,
  help,
  children,
}: {
  id: string
  label: string
  required?: boolean
  help?: ReactNode
  children: ReactNode
}) {
  return (
    <div className="field">
      <label className="label" htmlFor={id}>
        {label}
        {required && (
          <span className="req" aria-hidden="true">
            {' '}
            *
          </span>
        )}
      </label>
      {children}
      {help && <span className="help">{help}</span>}
    </div>
  )
}

/** Статус записи словом и точкой — тем же словарём, что и в списке. */
export function StatusBadge({ entity, status }: { entity: string; status: string | null }) {
  if (!status) return null
  return (
    <CellValue
      values={{ status }}
      column="status"
      descriptor={{ name: 'status', type: 'text', sortable: false, filterable: false }}
      entity={entity}
    />
  )
}

/**
 * Ссылка на запись по названию из механизма списков: название, а переход — только если
 * раздел цели есть у пользователя (иначе ссылка вела бы на отказ).
 */
export function RefLink({
  values,
  column,
  entity,
  avatar,
}: {
  values: Record<string, unknown>
  column: string
  entity: string
  avatar?: boolean
}) {
  const { user } = useApp()
  const id = text(values, column)
  const label = labelOf(values, column)
  if (!id || !label) return null
  const href = cardHref(user, entity, id)
  const content = (
    <>
      {avatar && (
        <span className="ava" aria-hidden="true">
          {initials(label)}
        </span>
      )}
      {label}
    </>
  )
  return href ? (
    <Link to={href} className="row" style={{ gap: 6 }}>
      {content}
    </Link>
  ) : (
    <span className="row" style={{ gap: 6 }}>
      {content}
    </span>
  )
}

/**
 * Названия записей по идентификаторам — механизмом списков.
 *
 * Пересечения отпусков, руководители узлов оргструктуры, отделы в истории назначений
 * приходят с сервера идентификаторами. Название берётся из списка сущности, а не своим
 * эндпоинтом: там уже работают область видимости и полевые права. Чего список не отдал, то
 * показывается короткой формой идентификатора — выдумать имя хуже, чем показать, что его нет.
 */
export function useRecordNames(entity: string, columns: string[], ids: (string | null | undefined)[]): Map<string, string> {
  const unique = [...new Set(ids.filter((id): id is string => typeof id === 'string' && id !== ''))].sort()
  const query = useQuery({
    queryKey: ['hr-names', entity, unique],
    enabled: unique.length > 0,
    retry: false,
    queryFn: ({ signal }) =>
      fetchPage(
        entity,
        {
          columns,
          filters: [{ column: 'id', operator: 'in', value: unique }],
          sort: [],
          pageSize: Math.min(unique.length, 200),
        },
        null,
        signal,
      ),
  })
  const names = new Map<string, string>()
  for (const row of query.data?.rows ?? []) {
    const name = columns
      .map((column) => row[column])
      .filter((part) => typeof part === 'string' && part !== '')
      .join(' ')
    if (name) names.set(row.id, name)
  }
  return names
}

/** Имена сотрудников: «Фамилия Имя», как их подписывает и список. */
export function useEmployeeNames(ids: (string | null | undefined)[]): Map<string, string> {
  return useRecordNames('hr.employee', ['last_name', 'first_name'], ids)
}

/** Имя по карте или короткий идентификатор. */
export function nameOf(names: Map<string, string>, id: string | null | undefined): string {
  if (!id) return '—'
  return names.get(id) ?? short(id)
}
